import { Box, Chip, makeStyles, Typography } from '@material-ui/core';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import { useParams } from '@reach/router';
import React, { useEffect, useState } from 'react';

import LoadingContainer from '../../../../../components/LoadingContainer';
import NoData from '../../../../../components/NoData';
import { api } from '../../../../../core/api';
import { getActiveDataset, getActiveDbVersion } from '../../../../../core/utils/utils';
import { useAppGoogleReCaptcha } from '../../../../../hooks/useAppGoogleReCaptcha';
import { useBenchmarkState } from '../../../../Benchmark/Context/BenchmarkContext';
import { useCompaniesState } from '../../../shared/CompanyContext/CompanyContext';
import { useErrorsDispatch } from '../../../shared/ErrorContext/ErrorContext';
import { ErrorContextActionTypes } from '../../../shared/ErrorContext/ErrorContext.types';
import { TabNamesEnum } from '../../CompanyTabs/CompanyTabs';

const KeywordsBlock: React.FC = () => {
  const classes = useStyles();
  const {
    company,
    companyGeneralInfo,
    clickedCompanyTabName,
    dataset,
    dbVersion,
  } = useCompaniesState();
  const [keywords, setKeywords] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { executeRecaptcha } = useAppGoogleReCaptcha();
  const dispatchErrors = useErrorsDispatch();
  const { companyUid, dataset: datasetParam, dbVersion: dbVersionParam } = useParams<{
    companyUid?: string;
    dataset?: string;
    dbVersion?: string;
  }>();
  const resolvedCompanyUid = companyUid || company?.companyInfo?.id;

  const { activeBenchmark } = useBenchmarkState();
  const datasetSource = datasetParam ?? dataset;
  const dbVersionSource = dbVersionParam ?? dbVersion;
  const activeDataset = getActiveDataset(datasetSource, activeBenchmark);
  const activeDbVersion = getActiveDbVersion(dbVersionSource, activeBenchmark);

  useEffect(() => {
    setIsLoading(true);
    if (
      companyGeneralInfo &&
      companyGeneralInfo.companyActivity &&
      companyGeneralInfo.companyActivity.keywords
    ) {
      setKeywords(companyGeneralInfo.companyActivity.keywords);
      setIsLoading(false);
    }
  }, [companyGeneralInfo]);

  useEffect(() => {
    setIsLoading(true);
    if (clickedCompanyTabName !== TabNamesEnum.COMPANY && resolvedCompanyUid) {
      const getKeywords = async () => {
        try {
          const recaptchaToken = await executeRecaptcha?.('search');
          const response = await api.company.getGeneralData(
            resolvedCompanyUid,
            recaptchaToken!,
            activeDataset,
            activeDbVersion,
          );
          if (response && response.companyActivity) {
            setKeywords(response.companyActivity.keywords ?? []);
            setIsLoading(false);
          }
        } catch (error) {
          dispatchErrors({
            type: ErrorContextActionTypes.SetError,
            payload: { error },
          });
        }
      };
      getKeywords();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedCompanyUid, activeDataset, activeDbVersion]);

  return (
    <Box className={classes.keywordsBlock}>
      {company && Object.keys(company).length ? (
        <>
          <Box className={classes.keywordsItems} data-cy="company-details-header">
            <Typography variant="h4">{company.companyInfo?.name}</Typography>
            <Typography variant="body1">
              {company.companyInfo?.country}
              <FiberManualRecordIcon /> {company.companyInfo?.companyNumber}
              <FiberManualRecordIcon /> {company.companyInfo?.statuses?.join(', ')}
              <FiberManualRecordIcon /> {company.companyInfo?.legalForm}
            </Typography>
          </Box>
          <Box className={classes.keywordsItems} data-cy="company-details-keywords">
            <Box className={classes.keywordsContainer}>
              {isLoading ? (
                <Box className={classes.progressBox}>
                  <LoadingContainer progressColor="inherit" />
                </Box>
              ) : (
                keywords?.map((item: string, index: number) => {
                  return <Chip label={item} className={classes.chipBox} key={index} />;
                })
              )}
            </Box>
          </Box>
        </>
      ) : (
        <Box className={classes.noData}>
          <NoData iconColor={'secondary'} />
        </Box>
      )}
    </Box>
  );
};

const useStyles = makeStyles((theme) => ({
  keywordsBlock: {
    width: 'calc(100% - 350px)',
    minHeight: '210px',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  noData: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  keywordsItems: {
    padding: theme.spacing(8),
    height: '105px',
    overflow: 'hidden',
    '&:first-child': {
      borderBottom: '1px solid #338ad1',
    },
    '& .MuiSvgIcon-root': {
      width: '7px',
      height: '7px',
      margin: '3px 5px 3px 10px',
    },
  },
  keywordsContainer: {
    overflow: 'hidden',
    position: 'relative',
    minHeight: '55px',
    maxHeight: '55px',
  },
  chipBox: {
    backgroundColor: '#338ad1',
    color: theme.palette.primary.contrastText,
    height: '22px',
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  progressBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

export default KeywordsBlock;
